import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { canPerformAction, hasPermission } from '../utils/permissionUtils';
import UserProfile from './UserProfile';
import StorageManagement from './StorageManagement';
import ManageUsers from './ManageUsers';
import ManageTips from './ManageTips';
import NewsManager from './NewsManager';
import ManageAudioClips from './ManageAudioClips';
import RehearsalManager from './RehearsalManager';
import BugReportDialog from './BugReportDialog';
import ManageIssues from './ManageIssues';
import ManageTelegramBot from './ManageTelegramBot';
import ManageGallery from './ManageGallery';
import ManageSettings from './ManageSettings';
import AIIntegration from './AIIntegration';
import ActivityLog from './ActivityLog';
import LoginDialog from './LoginDialog';
import RegisterDialog from './RegisterDialog';
import '../styles/auth.css';

const AuthButton = ({
  currentFileName = null,
  osmdInstance = null,
  visibleParts = [],
  onTipsUpdated = null,
  onTipPlacementModeChange = null,
  onLoadFileRequest = null,
  onNewsUpdated = null,
  onRegisterTrigger = null
}) => {
  const { user, loading, signIn, signOut } = useAuth();
  const { t } = useLanguage();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMusicXMLSubmenu, setShowMusicXMLSubmenu] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showStorage, setShowStorage] = useState(false);
  const [showManageUsers, setShowManageUsers] = useState(false);
  const [showManageTips, setShowManageTips] = useState(false);
  const [showNewsManager, setShowNewsManager] = useState(false);
  const [showManageAudioClips, setShowManageAudioClips] = useState(false);
  const [showRehearsalManager, setShowRehearsalManager] = useState(false);
  const [showBugReport, setShowBugReport] = useState(false);
  const [showManageIssues, setShowManageIssues] = useState(false);
  const [showManageTelegramBot, setShowManageTelegramBot] = useState(false);
  const [showManageGallery, setShowManageGallery] = useState(false);
  const [showManageSettings, setShowManageSettings] = useState(false);
  const [showAIIntegration, setShowAIIntegration] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Expose the register dialog trigger to parent components
  useEffect(() => {
    if (onRegisterTrigger) {
      onRegisterTrigger(() => () => setShowRegisterDialog(true));
    }
  }, [onRegisterTrigger]);

  // Load user profile data when user changes
  useEffect(() => {
    if (user?.id) {
      loadUserProfile();
    }
  }, [user]);

  const loadUserProfile = async (addDelay = false) => {
    setProfileLoading(true);
    try {
      // Add a small delay when refreshing after save to allow blob storage to update
      if (addDelay) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      // Add cache-busting parameter
      const timestamp = Date.now();
      const response = await fetch(`${API_BASE}/api/user?operation=getAccount&userId=${user.id}&_t=${timestamp}`);
      
      if (response.ok) {
        const profileData = await response.json();
        // console.log('AuthButton loaded profile data:', profileData);
        setUserProfile(profileData);
      } else {
        // console.log('AuthButton: No profile found, response status:', response.status);
        setUserProfile(null);
      }
    } catch (error) {
      // console.log('AuthButton: Error loading profile:', error);
      setUserProfile(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const getDisplayName = () => {
    if (profileLoading) return '...';
    return userProfile?.displayName || user?.name || t('auth.user');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (loading) {
    return <div className="auth-loading">{t('auth.loading')}</div>;
  }

  if (user) {
    return (
      <>
        <div className="auth-container" ref={dropdownRef}>
          <div 
            className="user-info clickable" 
            onClick={() => setShowDropdown(!showDropdown)}
          >
            <img 
              src={user.image} 
              alt={user.name} 
              className="user-avatar"
            />
            <span className="user-name">{getDisplayName()}</span>
            <svg 
              className={`dropdown-arrow ${showDropdown ? 'open' : ''}`} 
              width="14" 
              height="14" 
              viewBox="0 0 12 12"
            >
              <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            </svg>
          </div>

          {showDropdown && (
            <div className="user-dropdown">
              <button 
                className="dropdown-item"
                onClick={() => {
                  setShowProfile(true);
                  setShowDropdown(false);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
                {t('auth.profileSettings')}
              </button>
              
              {/* MusicXML Player Submenu - Only show if user has relevant permissions */}
              {(canPerformAction(user, 'manage-storage') || hasPermission(user, 'manage-tips')) && (
                <>
                  <button 
                    className="dropdown-item"
                    onClick={() => setShowMusicXMLSubmenu(!showMusicXMLSubmenu)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                      </svg>
                      {t('auth.musicXMLPlayer')}
                    </div>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ transform: showMusicXMLSubmenu ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', opacity: 0.8 }}>
                      <path d="M7 10l5 5 5-5z"/>
                    </svg>
                  </button>
                  
                  {/* Submenu items - shown inline */}
                  {showMusicXMLSubmenu && (
                    <>
                      {/* Manage Storage */}
                      {canPerformAction(user, 'manage-storage') && (
                        <button 
                          className="dropdown-item"
                          onClick={() => {
                            setShowStorage(true);
                            setShowDropdown(false);
                            setShowMusicXMLSubmenu(false);
                          }}
                          style={{ paddingLeft: '40px' }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 15v4H5v-4h14m1-2H4c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h16c.55 0 1-.45 1-1v-6c0-.55-.45-1-1-1zM19 5v4H5V5h14m1-2H4c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h16c.55 0 1-.45 1-1V4c0-.55-.45-1-1-1z"/>
                          </svg>
                          {t('auth.manageStorage')}
                        </button>
                      )}
                      
                      {/* Manage Tips */}
                      {hasPermission(user, 'manage-tips') && (
                        <button 
                          className="dropdown-item"
                          onClick={() => {
                            setShowManageTips(true);
                            setShowDropdown(false);
                            setShowMusicXMLSubmenu(false);
                          }}
                          style={{ paddingLeft: '40px' }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M9 11H7v6h2v-6zm4 0h-2v6h2v-6zm4 0h-2v6h2v-6zM4 19h16v2H4v-2zM20 4H4c-1.1 0-2 .9-2 2v3h20V6c0-1.1-.9-2-2-2zM3 14h18v-2H3v2z"/>
                          </svg>
                          {t('auth.manageTips')}
                        </button>
                      )}
                      
                      {/* Manage Audio Clips */}
                      <button 
                        className="dropdown-item"
                        onClick={() => {
                          setShowManageAudioClips(true);
                          setShowDropdown(false);
                          setShowMusicXMLSubmenu(false);
                        }}
                        style={{ paddingLeft: '40px' }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                        </svg>
                        {t('auth.manageAudioClips')}
                      </button>
                    </>
                  )}
                </>
              )}

              {/* News Management - Only show if user has permission */}
              {hasPermission(user, 'manage-news') && (
                <>
                  <button 
                    className="dropdown-item"
                    onClick={() => {
                      setShowNewsManager(true);
                      setShowDropdown(false);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9H9V9h10v2zm-4 4H9v-2h6v2zm4-8H9V5h10v2z"/>
                    </svg>
{t('auth.manageNews')}
                  </button>
                </>
              )}

              {/* Rehearsal Management - Only show if user has permission */}
              {hasPermission(user, 'manage-rehearsals') && (
                <>
                  <button 
                    className="dropdown-item"
                    onClick={() => {
                      setShowRehearsalManager(true);
                      setShowDropdown(false);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    {t('auth.manageRehearsals')}
                  </button>
                </>
              )}

              {/* Manage Users - Only show if user has permission */}
              {hasPermission(user, 'manage-users') && (
                <>
                  <button 
                    className="dropdown-item"
                    onClick={() => {
                      setShowManageUsers(true);
                      setShowDropdown(false);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9 13.75c-2.34 0-7 1.17-7 3.5V19h14v-1.75c0-2.33-4.66-3.5-7-3.5zM4.34 17c.84-.58 2.87-1.25 4.66-1.25s3.82.67 4.66 1.25H4.34zM9 12c1.93 0 3.5-1.57 3.5-3.5S10.93 5 9 5 5.5 6.57 5.5 8.5 7.07 12 9 12zm0-5c.83 0 1.5.67 1.5 1.5S9.83 10 9 10s-1.5-.67-1.5-1.5S8.17 7 9 7zm7.04 6.81c1.16.84 1.96 1.96 1.96 3.44V19h4v-1.75c0-2.02-3.5-3.17-5.96-3.44zM15 12c1.93 0 3.5-1.57 3.5-3.5S16.93 5 15 5c-.54 0-1.04.13-1.5.35.63.89 1 1.98 1 3.15s-.37 2.26-1 3.15c.46.22.96.35 1.5.35z"/>
                    </svg>
                    {t('auth.manageUsers')}
                  </button>
                </>
              )}

              {/* Activity Log - Only show if user has permission */}
              {hasPermission(user, 'view-activity-logs') && (
                <>
                  <button 
                    className="dropdown-item"
                    onClick={() => {
                      setShowActivityLog(true);
                      setShowDropdown(false);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M4 4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2h-8l-2-2H4zm0 4h16v8H4V8z"/>
                    </svg>
                    {t('auth.activityLog')}
                  </button>
                </>
              )}

              {/* Manage Gallery - Only show if user has permission */}
              {hasPermission(user, 'manage-gallery') && (
                <>
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      setShowManageGallery(true);
                      setShowDropdown(false);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                    </svg>
                    {t('auth.manageGallery')}
                  </button>
                </>
              )}

              {/* Manage Ensemble - Admin only */}
              {(user.role === 'admin' || user.role === 'super_admin' || user.role === 'super-admin') && (
                <>
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      window.location.href = '/ensemble/admin';
                      setShowDropdown(false);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6zm10 9h-2v-2h-2v2h-2v2h2v2h2v-2h2v-2z"/>
                    </svg>
                    Manage Ensemble
                  </button>
                </>
              )}

              {/* Manage Issues - Only show if user has permission */}
              {hasPermission(user, 'manage-issues') && (
                <>
                  <button 
                    className="dropdown-item"
                    onClick={() => {
                      setShowManageIssues(true);
                      setShowDropdown(false);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14,12H10V10H14M14,16H10V14H14M20,8L14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8M18,20H6V4H13V9H18V20Z" />
                    </svg>
                    {t('auth.manageIssues')}
                  </button>
                </>
              )}

              {/* Manage Telegram Bot - Only show if user has permission */}
              {hasPermission(user, 'manage-telegram-bot') && (
                <>
                  <button 
                    className="dropdown-item"
                    onClick={() => {
                      setShowManageTelegramBot(true);
                      setShowDropdown(false);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1.2 4.11c.13 0 .24-.04.35-.12.11-.08.16-.2.13-.34-.01-.07-.06-.13-.12-.16-.38-.17-.8-.26-1.24-.26-.41 0-.8.08-1.16.24-.36.16-.67.39-.91.68-.24.29-.43.63-.55 1.01-.12.38-.18.78-.18 1.2 0 .42.06.82.18 1.2.12.38.31.72.55 1.01.24.29.55.52.91.68.36.16.75.24 1.16.24.44 0 .86-.09 1.24-.26.06-.03.11-.09.12-.16.03-.14-.02-.26-.13-.34-.11-.08-.22-.12-.35-.12h-.02c-.28.11-.58.17-.88.17-.3 0-.58-.06-.85-.17-.27-.11-.5-.27-.69-.47-.19-.2-.34-.44-.44-.71-.1-.27-.15-.56-.15-.86 0-.3.05-.59.15-.86.1-.27.25-.51.44-.71.19-.2.42-.36.69-.47.27-.11.55-.17.85-.17.3 0 .6.06.88.17h.02z"/>
                    </svg>
{t('auth.manageTelegramBot')}
                  </button>
                </>
              )}

              {/* Manage Settings - Only show if user has permission */}
              {hasPermission(user, 'manage-settings') && (
                <>
                  <button 
                    className="dropdown-item"
                    onClick={() => {
                      setShowManageSettings(true);
                      setShowDropdown(false);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.82,11.69,4.82,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
                    </svg>
                    {t('auth.manageSettings')}
                  </button>
                </>
              )}
              
              {/* AI Integration - Available to all authenticated users */}
              <button 
                className="dropdown-item"
                onClick={() => {
                  setShowAIIntegration(true);
                  setShowDropdown(false);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1.2 14.4c-.49 0-.9-.41-.9-.9s.41-.9.9-.9.9.41.9.9-.41.9-.9.9zm2.5-6.5c-.3.3-.7.7-.7 1.1v.4c0 .28-.22.5-.5.5s-.5-.22-.5-.5v-.4c0-.8.4-1.5 1-2l.6-.6c.2-.2.3-.5.3-.8 0-.6-.4-1-1-1s-1 .4-1 1c0 .28-.22.5-.5.5s-.5-.22-.5-.5c0-1.1.9-2 2-2s2 .9 2 2c0 .7-.3 1.3-.8 1.8l-.6.6z"/>
                </svg>
                {t('auth.aiIntegration')}
              </button>
              
              {/* Bug Report - Available to all authenticated users */}
              <button 
                className="dropdown-item"
                onClick={() => {
                  setShowBugReport(true);
                  setShowDropdown(false);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14,12H10V10H14M14,16H10V14H14M20,8L14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8M18,20H6V4H13V9H18V20Z" />
                </svg>
                {t('bugReport.reportIssue')}
              </button>
              
              <div className="dropdown-divider"></div>
              <button 
                className="dropdown-item sign-out-item"
                onClick={() => {
                  signOut();
                  setShowDropdown(false);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
                </svg>
                {t('auth.signOut')}
              </button>
            </div>
          )}
        </div>

        <UserProfile 
          isOpen={showProfile} 
          onClose={() => setShowProfile(false)}
          onProfileUpdate={() => loadUserProfile(true)}
        />
        
        <StorageManagement
          isOpen={showStorage}
          onClose={() => setShowStorage(false)}
        />
        
        <ManageUsers
          isOpen={showManageUsers}
          onClose={() => setShowManageUsers(false)}
        />
        
        <ManageTips
          isOpen={showManageTips}
          onClose={() => setShowManageTips(false)}
          currentFileName={currentFileName}
          osmdInstance={osmdInstance}
          visibleParts={visibleParts}
          onTipsUpdated={onTipsUpdated}
          onTipPlacementModeChange={onTipPlacementModeChange}
          onLoadFileRequest={onLoadFileRequest}
        />
        
        <NewsManager
          isOpen={showNewsManager}
          onClose={() => {
            setShowNewsManager(false);
            // Notify parent component that news was updated
            if (onNewsUpdated) {
              onNewsUpdated();
            }
          }}
        />
        
        <ManageAudioClips
          isOpen={showManageAudioClips}
          onClose={() => setShowManageAudioClips(false)}
        />
        
        <RehearsalManager
          isOpen={showRehearsalManager}
          onClose={() => setShowRehearsalManager(false)}
        />
        
        <BugReportDialog
          isOpen={showBugReport}
          onClose={() => setShowBugReport(false)}
        />
        
        <ManageIssues
          isOpen={showManageIssues}
          onClose={() => setShowManageIssues(false)}
        />
        
        <ManageTelegramBot
          isOpen={showManageTelegramBot}
          onClose={() => setShowManageTelegramBot(false)}
        />
        
        <ManageGallery
          isOpen={showManageGallery}
          onClose={() => setShowManageGallery(false)}
          user={user}
        />
        
        <ManageSettings
          isOpen={showManageSettings}
          onClose={() => setShowManageSettings(false)}
        />
        
        <AIIntegration
          isOpen={showAIIntegration}
          onClose={() => setShowAIIntegration(false)}
        />
        
        <ActivityLog
          isOpen={showActivityLog}
          onClose={() => setShowActivityLog(false)}
        />
      </>
    );
  }

  return (
    <>
      <div className="auth-container">
        <div className="auth-buttons">
          <button onClick={signIn} className="google-signin-button">
            <svg className="google-icon" viewBox="0 0 24 24" width="18" height="18">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('auth.signInWithGoogle')}
          </button>
          
          <div className="email-auth-buttons">
            <button 
              onClick={() => setShowLoginDialog(true)} 
              className="email-signin-button"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
              </svg>
              {t('auth.signInWithEmail')}
            </button>
            
            <button 
              onClick={() => setShowRegisterDialog(true)} 
              className="email-signup-button"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
              {t('auth.createAccount')}
            </button>
          </div>
        </div>
      </div>

      <LoginDialog
        isOpen={showLoginDialog}
        onClose={() => setShowLoginDialog(false)}
        onSwitchToRegister={() => {
          setShowLoginDialog(false);
          setShowRegisterDialog(true);
        }}
      />

      <RegisterDialog
        isOpen={showRegisterDialog}
        onClose={() => setShowRegisterDialog(false)}
        onSwitchToLogin={() => {
          setShowRegisterDialog(false);
          setShowLoginDialog(true);
        }}
      />
    </>
  );
};

export default AuthButton;